import { manifest as tick } from '@titan/module-tick/manifest';
import {
  MODULE_KEYS,
  buildNavModel,
  isStaffSurface,
  type Actor,
  type Entitlements,
  type ModuleKey,
  type ModuleManifest,
  type NavModel,
} from '@titan/shared';

/**
 * The module registry. `pnpm new-module <key>` appends the import and the entry below.
 * Everything the console derives from modules reads this list.
 */
export const MANIFESTS: readonly ModuleManifest[] = [tick];

export const MODULE_REGISTRY: Partial<Record<ModuleKey, ModuleManifest>> = Object.fromEntries(MANIFESTS.map((m) => [m.key, m]));

/** Modules a client can see: entitlement toggle, matrix column, launcher card, nav. */
export const SHIPPED_MODULES: readonly ModuleKey[] = MANIFESTS.filter((m) => m.shipped).map((m) => m.key);

/** Modules that exist as code, shipped or not. Staff reach their screens through the nav. */
export const REGISTERED_MODULES: readonly ModuleKey[] = MANIFESTS.map((m) => m.key);

/** Nav for clients: shipped modules only. */
export const NAV_MODEL: NavModel = buildNavModel(MANIFESTS);

/** Nav for DinoCloud staff: every registered module, so in-development work is reachable. */
export const NAV_MODEL_STAFF: NavModel = buildNavModel(MANIFESTS, { includeUnshipped: true });

export function navModelFor(actor: Actor): NavModel {
  return isStaffSurface(actor) ? NAV_MODEL_STAFF : NAV_MODEL;
}

export function manifestFor(key: ModuleKey): ModuleManifest | null {
  return MODULE_REGISTRY[key] ?? null;
}

/** New tenants start entitled to shipped modules only. */
export function defaultEntitlements(): Entitlements {
  return Object.fromEntries(MODULE_KEYS.map((k) => [k, SHIPPED_MODULES.includes(k)])) as Entitlements;
}

/** Keys the handoff defines that have no code yet. Useful for the "in development" screens. */
export const UNREGISTERED_MODULES: readonly ModuleKey[] = MODULE_KEYS.filter((k) => !REGISTERED_MODULES.includes(k));
